// `LineEdits` (SuiteModel/LineEdits.swift): the edits that turn one text into another a whole line at a time (a line keeps
// its newline), from a longest common subsequence of lines. A compile preset is written this way, a setting a line, so two
// devices changing different settings merge into both changes rather than mixing their characters. Offsets here are UTF-16
// (JavaScript's, and Automerge's in JavaScript); the Swift's are Unicode scalars.

export interface LineEdit {
  start: number;
  delete: number;
  insert: string;
}

function lines(text: string): string[] {
  const out: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') {
      out.push(text.slice(start, i + 1));
      start = i + 1;
    }
  }
  if (start < text.length) out.push(text.slice(start));
  return out;
}

/** The edits in order of `start`; apply them from the last to the first. */
export function lineEdits(old: string, next: string): LineEdit[] {
  const a = lines(old), b = lines(next);
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const edits: LineEdit[] = [];
  let i = 0, j = 0, offset = 0;
  let pending: LineEdit | null = null;
  const flush = () => {
    if (pending && (pending.delete > 0 || pending.insert)) edits.push(pending);
    pending = null;
  };
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      flush();
      offset += a[i].length;
      i++;
      j++;
    } else if (j < b.length && (i === a.length || lcs[i][j + 1] >= lcs[i + 1][j])) {
      pending ??= { start: offset, delete: 0, insert: '' };
      pending.insert += b[j];
      j++;
    } else {
      pending ??= { start: offset, delete: 0, insert: '' };
      pending.delete += a[i].length;
      offset += a[i].length;
      i++;
    }
  }
  flush();
  return edits;
}
